/* GSAP-native replacement for the Splide.js carousel engine.
   Loaded once, globally (mirrors assets/scroll-component.js), and defines the
   same <carousel-slider> custom element / data-splide JSON contract the theme's
   section and snippet templates already author, so no template changes are
   required. Drag/momentum comes from GSAP Draggable + InertiaPlugin instead
   of Splide's internal pointer handling. */
(function () {
  if (customElements.get('carousel-slider')) return;

  var INERT_FOCUSABLE_SELECTOR = 'a[href], button, input, select, textarea, [tabindex]';
  /* An aria-hidden="true" slide must not contain any focusable descendant —
     leaving one tabbable still lets keyboard/AT users tab into content the
     slide is telling assistive tech doesn't exist (axe: aria-hidden-focus).
     Blurring the active element (done elsewhere) only handles the element
     that currently has focus; it does nothing about the rest of the slide's
     interactive children remaining in tab order. Stash each element's prior
     tabindex so it can be restored exactly (including "no attribute at all")
     when the slide becomes visible again, rather than assuming 0. */
  function setSlideInert(slide, inert) {
    var focusables = slide.querySelectorAll(INERT_FOCUSABLE_SELECTOR);
    for (var i = 0; i < focusables.length; i++) {
      var el = focusables[i];
      if (inert) {
        if (!el.hasAttribute('data-inert-tabindex')) {
          el.setAttribute('data-inert-tabindex', el.getAttribute('tabindex') || '');
        }
        el.setAttribute('tabindex', '-1');
      } else if (el.hasAttribute('data-inert-tabindex')) {
        var prior = el.getAttribute('data-inert-tabindex');
        if (prior) {
          el.setAttribute('tabindex', prior);
        } else {
          el.removeAttribute('tabindex');
        }
        el.removeAttribute('data-inert-tabindex');
      }
    }
  }

  var pluginsReady = false;
  function markSliderPluginsReady() {
    if (pluginsReady) return;
    pluginsReady = true;
    window.gsap.registerPlugin(window.Draggable, window.InertiaPlugin);
  }
  function selfPollGsapReady(cb, attempt) {
    attempt = attempt || 0;
    if (window.gsap && window.Draggable && window.InertiaPlugin) {
      markSliderPluginsReady();
      cb();
      return;
    }
    if (attempt >= 150) return;
    setTimeout(function () {
      selfPollGsapReady(cb, attempt + 1);
    }, 100);
  }
  /* layout/theme.liquid's window.onThemeGsapReady already runs a single
     shared poll for gsap/ScrollTrigger/Draggable/InertiaPlugin and registers
     them centrally — reuse that instead of running a second, independent
     poll loop in parallel on every page that has it. layout/password.liquid
     loads this file too but never defines onThemeGsapReady (it doesn't load
     ScrollTrigger at all), so fall back to this module's own self-contained
     poll there, which only waits on the plugins this carousel actually needs. */
  function whenGsapReady(cb) {
    if (window.onThemeGsapReady) {
      window.onThemeGsapReady(function () {
        markSliderPluginsReady();
        cb();
      });
    } else {
      selfPollGsapReady(cb);
    }
  }

  function toUnit(value) {
    if (value === undefined || value === null || value === '') return '';
    return typeof value === 'number' ? value + 'px' : String(value);
  }

  function toPx(value, axisSize) {
    if (value === undefined || value === null || value === '') return 0;
    if (typeof value === 'number') return value;
    var str = String(value).trim();
    if (str.slice(-1) === '%') return (parseFloat(str) / 100) * axisSize;
    return parseFloat(str) || 0;
  }

  /* cloneNode(true) duplicates every id in the subtree, not just the clone
     root's own id — a slide with an embedded video/iframe/form produces
     duplicate ids across every loop clone. Strip the root and every
     descendant. */
  function stripIds(root) {
    root.removeAttribute('id');
    var withIds = root.querySelectorAll('[id]');
    for (var i = 0; i < withIds.length; i++) withIds[i].removeAttribute('id');
  }

  /* ---------------------------------------------------------------------
     Slider: one instance per .splide element. Public surface intentionally
     flat (index/length/options/slides/go/on/sync/state) instead of Splide's
     nested Components API — see plan notes for the one external call site
     (sections/main-product.liquid) that consumes this. */
  function Slider(root, baseOptions) {
    this.root = root;
    this.track = root.querySelector('.splide__track');
    this.list = root.querySelector('.splide__list');
    this.baseOptions = baseOptions || {};
    this.options = Object.assign({}, this.baseOptions);
    this.cloneCount = 0;
    this._index = 0;
    this._state = 'idle';
    this._listeners = {};
    this._synced = [];
    this._mounted = false;
    this._lastMoveWasUser = false;
    /* Bumped on every go() and on drag start; a pending settle/realign whose
       captured generation no longer matches when it fires is stale (superseded
       by a newer go() or a user grabbing the slider mid-transition) and skips
       itself instead of stomping whatever now owns the list's transform. */
    this._settleGeneration = 0;
    this._reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (window.matchMedia) {
      var self = this;
      var rmQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      var rmHandler = function (e) { self._reducedMotion = e.matches; };
      if (rmQuery.addEventListener) rmQuery.addEventListener('change', rmHandler);
      this._rmQuery = rmQuery;
      this._rmHandler = rmHandler;
    }
  }

  Slider.prototype.on = function (eventNames, cb) {
    var self = this;
    eventNames.split(/\s+/).forEach(function (name) {
      if (!name) return;
      (self._listeners[name] = self._listeners[name] || []).push(cb);
    });
    return this;
  };

  Slider.prototype.off = function (eventNames, cb) {
    var self = this;
    if (!eventNames) {
      this._listeners = {};
      return this;
    }
    eventNames.split(/\s+/).forEach(function (name) {
      if (!name || !self._listeners[name]) return;
      if (typeof cb === 'function') {
        self._listeners[name] = self._listeners[name].filter(function (fn) {
          return fn !== cb;
        });
      } else {
        delete self._listeners[name];
      }
    });
    return this;
  };

  Slider.prototype.emit = function (name, detail) {
    (this._listeners[name] || []).forEach(function (cb) {
      cb(detail);
    });
  };

  Object.defineProperty(Slider.prototype, 'index', { get: function () { return this._index; } });
  Object.defineProperty(Slider.prototype, 'length', { get: function () { return this.realSlides ? this.realSlides.length : 0; } });
  Object.defineProperty(Slider.prototype, 'slides', {
    get: function () {
      var self = this;
      return (this.realSlides || []).map(function (el, i) {
        return { index: i, el: el, slide: el };
      });
    }
  });
  Object.defineProperty(Slider.prototype, 'state', {
    get: function () {
      var self = this;
      return { isIdle: this._state === 'idle', is: function (s) { return s === self._state; } };
    }
  });

  Slider.STATES = { IDLE: 'idle', MOVING: 'moving' };

  Slider.prototype.isOverflow = function () {
    var opts = this.options;
    var trackSize = opts.direction === 'ttb' ? this.track.clientHeight : this.track.clientWidth;
    var contentSize = opts.direction === 'ttb' ? this.list.scrollHeight : this.list.scrollWidth;
    return contentSize - trackSize > 1;
  };

  Slider.prototype.resolveOptions = function () {
    var resolved = Object.assign({}, this.baseOptions);
    var breakpoints = this.baseOptions.breakpoints;
    if (breakpoints) {
      var keys = Object.keys(breakpoints).map(Number).sort(function (a, b) { return a - b; });
      var width = window.innerWidth;
      keys.forEach(function (k) {
        if (width >= k) resolved = Object.assign(resolved, breakpoints[k]);
      });
    }
    this.options = resolved;
    return resolved;
  };

  Slider.prototype.mount = function () {
    this._mounted = true;
    this.root.classList.add('is-initialized');
    this.realSlides = Array.prototype.slice.call(this.list.querySelectorAll(':scope > .splide__slide'));
    this.list.classList.remove('splide-slider--h-scroll', 'splide-slider--v-scroll');
    this.layout();
    this.bindDrag();
    this.bindKeyboard();
    this.list.setAttribute('aria-live', 'off');
    this.root.setAttribute('role', 'region');
    this.root.setAttribute('aria-roledescription', 'carousel');
    var self = this;
    /* bindDrag() captures its drag axis (x vs y) from options.direction at
       bind time. If a breakpoint flips direction (e.g. row on desktop,
       column on mobile), re-running layout() alone leaves Draggable bound to
       the old axis. bindDrag() already kills its previous Draggable
       instance before creating a new one, so re-calling it here is safe and
       keeps the drag axis in sync with the resolved breakpoint options. */
    this._onResize = debounce(function () {
      self.layout();
      self.bindDrag();
    }, 150);
    window.addEventListener('resize', this._onResize);
    this.on('mounted moved updated', function () { self.refreshPagination(); });
    this.emit('mounted');
    if (this.options.autoplay) this.startAutoplay();
  };

  function debounce(fn, wait) {
    var t;
    return function () {
      clearTimeout(t);
      var args = arguments;
      t = setTimeout(function () { fn.apply(null, args); }, wait);
    };
  }

  Slider.prototype.layout = function () {
    var opts = this.resolveOptions();
    var isTtb = opts.direction === 'ttb';
    var isFade = opts.type === 'fade';

    this.rebuildClones(opts);

    /* Fade slides don't travel — they're stacked (however the consumer's own
       CSS does that: grid-area sharing, absolute positioning, etc.) and only
       cross-fade opacity. Forcing flex/gap/widths here would fight whatever
       stacking technique the consumer already has in place, so leave the
       list/track/slide box model completely alone for fade and let GSAP own
       opacity via setFadeState() instead. */
    if (!isFade) {
      this.list.style.display = 'flex';
      this.list.style.flexDirection = isTtb ? 'column' : 'row';
      var gap = toUnit(opts.gap != null ? opts.gap : 0);
      this.list.style.gap = gap;

      var perPage = opts.autoWidth ? null : opts.perPage || 1;
      var allSlides = Array.prototype.slice.call(this.list.querySelectorAll('.splide__slide'));
      allSlides.forEach(function (slide) {
        if (opts.fixedWidth) {
          slide.style.flex = '0 0 ' + toUnit(opts.fixedWidth);
          slide.style.width = toUnit(opts.fixedWidth);
        } else if (opts.autoWidth) {
          slide.style.flex = '0 0 auto';
          slide.style.width = '';
        } else if (!isTtb) {
          /* perPage divides the cross-axis (width) for row layouts. ttb layouts
             in this theme only ever run perPage:1 + autoHeight, sized by the
             slide's own intrinsic content height (see updateAutoHeight) — do
             NOT divide height by perPage here, that would make the slide's
             height a percentage of the track, which updateAutoHeight sets FROM
             the slide's height, i.e. a circular dependency. */
          var pct = 'calc((100% - (' + (perPage - 1) + ') * ' + (gap || '0px') + ') / ' + perPage + ')';
          slide.style.flex = '0 0 ' + pct;
          slide.style.width = pct;
        } else {
          slide.style.flex = '0 0 100%';
          slide.style.width = '100%';
        }
        if (opts.fixedHeight) slide.style.height = toUnit(opts.fixedHeight);
      });
    }

    if (opts.heightRatio) {
      var trackWidth = this.track.clientWidth;
      this.track.style.height = trackWidth * opts.heightRatio + 'px';
    }
    if (isFade) {
      this.track.classList.add('splide__track--fade');
    } else {
      this.track.classList.remove('splide__track--fade');
    }
    if (isTtb) this.track.classList.add('splide__track--ttb');
    else this.track.classList.remove('splide__track--ttb');

    this.computeOffsets();
    this.updateActiveClass();
    this.updatePosition(false);
    if (opts.autoHeight) this.updateAutoHeight();
    this.buildPagination();
    this.emit('updated');
    this.emit('resize');
  };

  Slider.prototype.rebuildClones = function (opts) {
    Array.prototype.slice.call(this.list.querySelectorAll('[data-slide-clone="true"]')).forEach(function (el) {
      el.remove();
    });
    if (opts.type !== 'loop' || !this.realSlides || this.realSlides.length < 2) {
      this.cloneCount = 0;
      return;
    }
    /* opts.perPage only describes percentage-of-track layouts; fixedWidth
       carousels (e.g. image-gallery) never set it, so it silently defaulted
       to 1 here and capped the clone buffer at ~2 slides regardless of how
       many fixedWidth cards actually fit across the track. On a wide
       viewport that starves loop dragging of runway — the Draggable bounds
       below are built from exactly these clones, so a drag hits a hard wall
       after only ~2 slides instead of having a full track's worth to travel.
       Estimate the visible count from the configured fixedWidth + gap so the
       clone buffer scales with the real viewport instead. */
    var perPage = opts.perPage || 1;
    if (!opts.autoWidth && opts.fixedWidth) {
      var isTtb = opts.direction === 'ttb';
      var trackSize = isTtb ? this.track.clientHeight : this.track.clientWidth;
      var slideSize = toPx(opts.fixedWidth, trackSize);
      var gapVal = toPx(opts.gap, trackSize);
      if (slideSize > 0) perPage = Math.max(perPage, trackSize / (slideSize + gapVal));
    }
    var count = Math.min(this.realSlides.length, Math.ceil(perPage) + 1);
    this.cloneCount = count;
    var head = this.realSlides.slice(0, count);
    var tail = this.realSlides.slice(this.realSlides.length - count);
    var self = this;
    var sanitizeClone = function (clone) {
      clone.setAttribute('data-slide-clone', 'true');
      clone.setAttribute('aria-hidden', 'true');
      setSlideInert(clone, true);
      stripIds(clone);
      clone.style.opacity = '1';
      clone.style.visibility = '';
      clone.style.transform = '';
      if (window.gsap) {
        window.gsap.set(clone, { opacity: 1, y: 0, x: 0, scale: 1, clearProps: 'transform' });
      }
    };
    tail.slice().reverse().forEach(function (el) {
      var clone = el.cloneNode(true);
      sanitizeClone(clone);
      self.list.insertBefore(clone, self.list.firstChild);
    });
    head.forEach(function (el) {
      var clone = el.cloneNode(true);
      sanitizeClone(clone);
      self.list.appendChild(clone);
    });
  };

  Slider.prototype.domIndexFor = function (realIndex) {
    return realIndex + this.cloneCount;
  };

  Slider.prototype.allDomSlides = function () {
    return Array.prototype.slice.call(this.list.querySelectorAll('.splide__slide'));
  };

  /* Prefix-sum pass over slide geometry, computed once per layout() call
     instead of on every drag frame / snap check. translateFor and
     snapPositions used to each re-walk the slide list from scratch —
     O(n) getBoundingClientRect reads per call, called O(n) times during
     drag/settle, for an O(n^2) reflow-inducing hot path. Reading every
     slide's rect exactly once here and caching the result makes both
     callers O(1)/O(n) lookups against data already in hand. */
  Slider.prototype.computeOffsets = function () {
    var opts = this.options;
    var isTtb = opts.direction === 'ttb';
    var slides = this.allDomSlides();
    var trackSize = isTtb ? this.track.clientHeight : this.track.clientWidth;
    var gapVal = toPx(opts.gap, trackSize);
    var offsets = [];
    var running = 0;
    for (var i = 0; i < slides.length; i++) {
      var rect = slides[i].getBoundingClientRect();
      var slideSize = isTtb ? rect.height : rect.width;
      var offset = running;
      if (opts.focus === 'center') offset -= (trackSize - slideSize) / 2;
      offsets.push(-offset);
      running += slideSize + gapVal;
    }
    /* running now includes one trailing gap after the last slide that no
       further slide follows — drop it so this reflects the list's real
       scrollable width. Per-slide indices (perPage > 1) otherwise let the
       final autoplay tick(s) land on the last single-slide index rather
       than the last full page, translating past the list's true right edge
       and leaving empty track space beyond the content. Clamping every
       offset to that true boundary stops it exactly at the max valid
       position without touching the tween itself (duration/ease/interval
       are untouched) or the focus:'center' offset at the opposite end. */
    var contentSize = slides.length ? running - gapVal : 0;
    var maxOffset = Math.min(0, trackSize - contentSize);
    for (var j = 0; j < offsets.length; j++) {
      if (offsets[j] < maxOffset) offsets[j] = maxOffset;
    }
    this._offsets = offsets;
    return offsets;
  };

  Slider.prototype.translateFor = function (domIndex) {
    if (!this._offsets) this.computeOffsets();
    var value = this._offsets[domIndex];
    return value === undefined ? 0 : value;
  };

  Slider.prototype.snapPositions = function () {
    if (!this._offsets) this.computeOffsets();
    return this._offsets.slice();
  };

  /* Fade type never translates the list — slides are stacked by the
     consumer's own CSS and only cross-fade opacity/visibility via GSAP
     autoAlpha, mirroring the crossfade technique featured-stories.liquid
     already used before it moved onto this shared engine. animate:false
     (mount/relayout) sets state instantly; animate:true (go()) tweens it. */
  /* Returns the incoming (about-to-be-active) slide's tween, if any — its
     duration/ease matches the outgoing slide's exactly, so its onComplete is
     representative of when the whole crossfade visually finishes. go() hangs
     the loop-realign / 'moved' emission off that instead of guessing a delay. */
  Slider.prototype.setFadeState = function (animate) {
    var domIndex = this.domIndexFor(this._index);
    var slides = this.allDomSlides();
    var duration = this._reducedMotion ? 0.01 : 0.5;
    var activeTween = null;
    slides.forEach(function (slide, i) {
      var isActive = i === domIndex;
      if (!window.gsap) {
        slide.style.opacity = isActive ? '1' : '0';
        slide.style.visibility = isActive ? '' : 'hidden';
        return;
      }
      if (animate) {
        var tween = window.gsap.to(slide, { autoAlpha: isActive ? 1 : 0, duration: duration, ease: 'power2.inOut', overwrite: 'auto' });
        if (isActive) activeTween = tween;
      } else {
        window.gsap.set(slide, { autoAlpha: isActive ? 1 : 0 });
      }
    });
    return activeTween;
  };

  Slider.prototype.animateToDomIndex = function (domIndex, animate) {
    if (this.options.type === 'fade') return this.setFadeState(animate);
    var isTtb = this.options.direction === 'ttb';
    var value = this.translateFor(domIndex);
    var duration = this._reducedMotion ? 0.01 : (animate ? 0.5 : 0);
    if (window.gsap) {
      var vars = { duration: duration, ease: 'power3.out', overwrite: 'auto', force3D: true };
      vars[isTtb ? 'y' : 'x'] = value;
      return window.gsap.to(this.list, vars);
    } else {
      this.list.style.transform = isTtb ? 'translateY(' + value + 'px)' : 'translateX(' + value + 'px)';
      return null;
    }
  };

  Slider.prototype.jumpToDomIndex = function (domIndex) {
    if (this.options.type === 'fade') { this.setFadeState(false); return; }
    var isTtb = this.options.direction === 'ttb';
    var value = this.translateFor(domIndex);
    if (window.gsap) {
      var vars = { overwrite: 'auto' };
      vars[isTtb ? 'y' : 'x'] = value;
      window.gsap.set(this.list, vars);
    } else {
      this.list.style.transform = isTtb ? 'translateY(' + value + 'px)' : 'translateX(' + value + 'px)';
    }
  };

  /* Direct-to-real-position helper for callers that never need to animate
     through the clone region (initial mount, relayout, drag settle). */
  Slider.prototype.updatePosition = function (animate) {
    this.animateToDomIndex(this.domIndexFor(this._index), animate);
  };

  Slider.prototype.updateActiveClass = function () {
    var domIndex = this.domIndexFor(this._index);
    var slides = this.allDomSlides();
    slides.forEach(function (slide, i) {
      var isActive = i === domIndex;
      slide.classList.toggle('is-active', isActive);
      /* A slide can still hold focus when it's about to be hidden (e.g. the
         user just clicked a control inside it that navigates elsewhere) —
         setting aria-hidden on an ancestor of the focused element is an
         accessibility violation browsers reject (and warn about), so blur
         it first rather than leaving focus trapped in hidden content. */
      if (!isActive && slide.contains(document.activeElement)) {
        document.activeElement.blur();
      }
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      setSlideInert(slide, !isActive);
    });
    var real = this.realSlides || [];
    real.forEach(function (slide, i) {
      slide.setAttribute('role', slide.getAttribute('role') || 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', (i + 1) + ' of ' + real.length);
    });
  };

  Slider.prototype.updateAutoHeight = function () {
    var domIndex = this.domIndexFor(this._index);
    var slide = this.allDomSlides()[domIndex];
    if (!slide) return;
    this.track.style.height = slide.offsetHeight + 'px';
  };

  Slider.prototype.buildPagination = function () {
    var existing = this.root.querySelector('.splide__pagination');
    if (!this.options.pagination) {
      if (existing) existing.remove();
      return;
    }
    if (existing) existing.remove();
    var self = this;
    var ul = document.createElement('ul');
    ul.className = 'splide__pagination';
    for (var i = 0; i < this.length; i++) {
      (function (i) {
        var li = document.createElement('li');
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'splide__pagination__page';
        btn.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        btn.addEventListener('click', function () {
          self._lastMoveWasUser = true;
          self.go(i);
        });
        li.appendChild(btn);
        ul.appendChild(li);
      })(i);
    }
    this.track.insertAdjacentElement('afterend', ul);
    this._paginationEl = ul;
    this.refreshPagination();
  };

  Slider.prototype.refreshPagination = function () {
    if (!this._paginationEl) return;
    var self = this;
    Array.prototype.forEach.call(this._paginationEl.querySelectorAll('.splide__pagination__page'), function (btn, i) {
      btn.classList.toggle('is-active', i === self._index);
    });
  };

  Slider.prototype.go = function (target, opts) {
    opts = opts || {};
    var len = this.length;
    if (!len) return;
    var isLoop = this.options.type === 'loop';
    var rewind = !!this.options.rewind;
    var raw = this._index;
    if (target === '>' || target === '+1') raw = this._index + 1;
    else if (target === '<' || target === '-1') raw = this._index - 1;
    else raw = target;

    var wrapped = raw;
    /* animateDomIndex !== null only for the single-step loop-wrap case, so the
       track visibly slides forward/back into the clone region instead of
       jumping backward across the whole track. Direct jumps (pagination,
       thumbnail click, goTo) go straight to the real slide's position. */
    var animateDomIndex = null;
    if (isLoop) {
      wrapped = ((raw % len) + len) % len;
      if (raw >= len && raw < len + this.cloneCount && this.cloneCount > 0) {
        animateDomIndex = raw + this.cloneCount;
      } else if (raw < 0 && raw >= -this.cloneCount && this.cloneCount > 0) {
        animateDomIndex = raw + this.cloneCount;
      }
    } else if (raw >= len) {
      wrapped = rewind ? 0 : len - 1;
    } else if (raw < 0) {
      wrapped = rewind ? len - 1 : 0;
    }
    var realDomIndex = this.domIndexFor(wrapped);
    if (animateDomIndex === null) animateDomIndex = realDomIndex;
    var needsRealign = animateDomIndex !== realDomIndex;

    this._index = wrapped;
    this._state = 'moving';
    this._lastMoveWasUser = !!opts.userInitiated || this._lastMoveWasUser;
    this.list.setAttribute('aria-live', this._lastMoveWasUser ? 'polite' : 'off');
    this.updateActiveClass();
    var tween = this.animateToDomIndex(animateDomIndex, !opts.silent);
    if (this.options.autoHeight) this.updateAutoHeight();
    var self = this;
    /* Rapid/held next-arrow clicks (or autoplay ticking again) call go()
       again before the previous call's settle fires. Bumping the generation
       here — and comparing it when the settle actually runs — makes any
       stale settle a no-op instead of instantly snapping the list to ITS
       realDomIndex, which by then no longer matches where the newest call
       animated to (visibly glitching mid-tween). The same guard also covers
       a user grabbing the slider mid-transition: bindDrag()'s onDragStart
       bumps this too, so a delayed loop-realign can never stomp a live drag. */
    var gen = ++this._settleGeneration;
    var finishSettle = function () {
      if (self._settleGeneration !== gen) return;
      if (needsRealign) self.jumpToDomIndex(realDomIndex);
      self._state = 'idle';
      self.emit('moved', { index: self._index });
    };
    /* Prefer the real tween's completion over a guessed delay — a fixed
       520ms was only ever an approximation of the 0.5s tween duration, and
       being decoupled from the actual animation is exactly what let a
       'moved' emission (and the idle state callers like featured-stories
       gate manual navigation on) land noticeably late or race a new
       interaction. Falls back to a timer only when there's no tween to hang
       off (GSAP unavailable, or the silent/no-op sync path). */
    if (tween && typeof tween.eventCallback === 'function') {
      tween.eventCallback('onComplete', finishSettle);
    } else {
      setTimeout(finishSettle, opts.silent ? 0 : (this._reducedMotion ? 20 : 520));
    }

    if (!opts.fromSync) {
      this._synced.forEach(function (other) { other.go(self._index, { silent: true, fromSync: true }); });
    }
    this._lastMoveWasUser = false;
  };

  Slider.prototype.sync = function (other) {
    this._synced.push(other);
    other._synced.push(this);
  };

  Slider.prototype.startAutoplay = function () {
    this.stopAutoplay();
    if (this._reducedMotion) return;
    var self = this;
    var interval = this.options.interval || 3000;
    this._autoplayTimer = setInterval(function () {
      if (self._paused) return;
      self.go('>');
    }, interval);
  };

  Slider.prototype.stopAutoplay = function () {
    if (this._autoplayTimer) clearInterval(this._autoplayTimer);
    this._autoplayTimer = null;
  };

  Slider.prototype.pauseAutoplay = function () { this._paused = true; };
  Slider.prototype.resumeAutoplay = function () { this._paused = false; };

  Slider.prototype.bindKeyboard = function () {
    var self = this;
    var isTtb = this.options.direction === 'ttb';
    this.root.addEventListener('keydown', function (e) {
      var prevKey = isTtb ? 'ArrowUp' : 'ArrowLeft';
      var nextKey = isTtb ? 'ArrowDown' : 'ArrowRight';
      if (e.key === prevKey) { e.preventDefault(); self.go('<', { userInitiated: true }); }
      else if (e.key === nextKey) { e.preventDefault(); self.go('>', { userInitiated: true }); }
    });
  };

  Slider.prototype.bindDrag = function () {
    if (this.options.drag === false || !window.Draggable) return;
    var self = this;
    var isTtb = this.options.direction === 'ttb';
    var prop = isTtb ? 'y' : 'x';
    if (this._draggable) this._draggable.forEach(function (d) { d.kill(); });
    if (!this._offsets) this.computeOffsets();
    var offsets = (this._offsets && this._offsets.length) ? this._offsets : [0];
    /* Bounds double as the fix for two related gaps: (1) edgeResistance is
       documented to only take effect when `bounds` is also set, so without
       this the 0.65 edge resistance configured below was silently a no-op;
       (2) InertiaPlugin clamps its projected fling target to stay inside
       bounds, so a hard/fast throw can no longer overshoot into the empty
       track space past the first/last rendered clone before snap corrects
       it. minDuration/maxDuration keep the throw tween itself from feeling
       instant on a tiny flick or unnaturally long on a hard one. */
    var bounds = {};
    bounds[isTtb ? 'minY' : 'minX'] = Math.min.apply(null, offsets);
    bounds[isTtb ? 'maxY' : 'maxX'] = Math.max.apply(null, offsets);
    var snap = {};
    snap[prop] = function (value) { return self.nearestSnapValue(value); };
    /* onPress pauses autoplay unconditionally (it fires on every pointerdown,
       drag or not), but a plain click/tap that never crosses the drag
       threshold never fires onDragStart/onThrowComplete — only onPress then
       onRelease. Resuming solely from onThrowComplete left autoplay paused
       forever after any such non-drag interaction. didDrag distinguishes the
       two cases so a plain click resumes immediately on release, while a real
       drag still waits for its throw to actually settle. */
    var didDrag = false;
    var vars = {
      type: prop,
      inertia: true,
      allowNativeTouchScrolling: true,
      bounds: bounds,
      edgeResistance: this.options.type === 'loop' ? 0 : 0.65,
      dragResistance: 0,
      minDuration: 0.2,
      maxDuration: 1.2,
      cursor: 'grab',
      snap: snap,
      onPress: function () { didDrag = false; self.pauseAutoplay(); },
      onDragStart: function () {
        didDrag = true;
        self._state = 'moving';
        // Invalidate any pending go()-originated loop-realign — see go()'s
        // _settleGeneration guard — so it can't land mid-drag and yank the
        // list out from under the user's finger.
        self._settleGeneration++;
      },
      onThrowComplete: function () {
        self.settleAfterDrag(this[prop]);
        self.resumeAutoplay();
      },
      onRelease: function () { if (!didDrag) self.resumeAutoplay(); }
    };
    this._draggable = window.Draggable.create(this.list, vars);
  };

  Slider.prototype.nearestSnapValue = function (value) {
    var positions = this.snapPositions();
    var closest = positions[0];
    var min = Infinity;
    positions.forEach(function (p) {
      var d = Math.abs(p - value);
      if (d < min) { min = d; closest = p; }
    });
    return closest;
  };

  Slider.prototype.settleAfterDrag = function (finalValue) {
    var positions = this.snapPositions();
    var domIndex = 0;
    var min = Infinity;
    positions.forEach(function (p, i) {
      var d = Math.abs(p - finalValue);
      if (d < min) { min = d; domIndex = i; }
    });
    var realIndex = domIndex - this.cloneCount;
    var len = this.length;
    realIndex = ((realIndex % len) + len) % len;
    this._index = realIndex;
    this._state = 'idle';
    this.updateActiveClass();
    this.updatePosition(false);
    this.emit('moved', { index: this._index });
    this._synced.forEach(function (other) { other.go(realIndex, { silent: true, fromSync: true }); });
  };

  Slider.prototype.destroy = function () {
    this.stopAutoplay();
    this._settleGeneration++; // invalidate any pending settle/realign
    if (this._draggable) this._draggable.forEach(function (d) { d.kill(); });
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    if (this._rmQuery && this._rmQuery.removeEventListener) this._rmQuery.removeEventListener('change', this._rmHandler);
  };

  /* ---------------------------------------------------------------------
     <carousel-slider> custom element: orchestration ported from the previous
     Splide-based implementation (bindExtraControls, centered-alignment,
     video autoplay, hover-to-play, intersection gating, goTo), rewired
     against the flat Slider API above instead of Splide's Components tree. */
  function defineCarouselSlider() {
    class CarouselSliderEl extends HTMLElement {
      connectedCallback() {
        whenGsapReady(this.initialize.bind(this));
      }
      disconnectedCallback() {
        if (this.slider) this.slider.destroy();
        if (this._syncSlider) this._syncSlider.destroy();
        if (this._observer) this._observer.disconnect();
      }
      initialize() {
        this.slider = null;
        this._syncSlider = null;
        var self = this;

        var bindExtraControls = function (rootEl, slider) {
          var searchContainer = rootEl.closest('.section-wrapper, .featured-collection_wrapper') || rootEl;
          var allPrev = searchContainer.querySelectorAll('.splide__arrow--prev');
          var allNext = searchContainer.querySelectorAll('.splide__arrow--next');
          allPrev.forEach(function (btn) {
            btn.addEventListener('click', function (e) {
              e.preventDefault();
              slider.go('<', { userInitiated: true });
            });
          });
          allNext.forEach(function (btn) {
            btn.addEventListener('click', function (e) {
              e.preventDefault();
              slider.go('>', { userInitiated: true });
            });
          });
          var updateControlsState = function () {
            var isLoop = slider.options.type === 'loop';
            var isFirst = slider.index === 0;
            var perPage = slider.options.perPage || 1;
            var isLast = slider.index >= slider.length - perPage;
            var controlsWrappers = searchContainer.querySelectorAll('.splide-controls, .splide__arrows');
            if (controlsWrappers.length) {
              controlsWrappers.forEach(function (ctrl) {
                ctrl.style.display = (slider.length <= 1 || slider.length <= perPage) ? 'none' : '';
              });
            }
            allPrev.forEach(function (btn) {
              if (!isLoop && isFirst) btn.setAttribute('disabled', 'disabled');
              else btn.removeAttribute('disabled');
            });
            allNext.forEach(function (btn) {
              if (!isLoop && isLast) btn.setAttribute('disabled', 'disabled');
              else btn.removeAttribute('disabled');
            });
          };
          var updateMediaHeight = function () {
            var mediaWraps = self.querySelectorAll('.product-card-new_media-wrap, .product-card-new_top-wrap, .card_media-wrap');
            var maxHeight = 0;
            mediaWraps.forEach(function (wrap) {
              var h = wrap.getBoundingClientRect().height;
              if (h > maxHeight) maxHeight = h;
            });
            if (maxHeight > 0) self.style.setProperty('--media-height', maxHeight + 'px');
          };
          slider.on('mounted moved updated refresh resize', updateMediaHeight);
          slider.on('mounted moved updated refresh resize', updateControlsState);
          updateControlsState();
          updateMediaHeight();
          setTimeout(updateMediaHeight, 100);
          setTimeout(updateMediaHeight, 300);
        };

        var initializeSlider = function (element, options, autoMount) {
          var slider = new Slider(element, options);
          var mountSlider = function () {
            slider.mount();
            bindExtraControls(self, slider);
          };
          if (autoMount !== false) mountSlider();
          slider.on('inactive', function (slide) { CarouselSliderEl.inactiveEvent(slide); });
          return { slider: slider, mount: mountSlider };
        };

        switch (this.dataset.sliderType) {
          case 'sync': {
            var mpElem = this.querySelector('[data-main-thumbs]');
            var swElem = this.querySelector('[data-switcher-thumbs]');
            if (!mpElem || !swElem) return;
            if (window.innerWidth >= 768) {
              mpElem.classList.add('is-initialized');
              swElem.classList.add('is-initialized');
              var mpList = mpElem.querySelector('.splide__list');
              var swList = swElem.querySelector('.splide__list');
              if (mpList) mpList.classList.remove('splide-slider--h-scroll', 'splide-slider--v-scroll');
              if (swList) swList.classList.remove('splide-slider--h-scroll', 'splide-slider--v-scroll');
              return;
            }
            var mpOpts = JSON.parse(mpElem.getAttribute('data-splide'));
            var swOpts = JSON.parse(swElem.getAttribute('data-splide'));
            var mpInstance = initializeSlider(mpElem, mpOpts, false);
            var swInstance = initializeSlider(swElem, swOpts, false);
            mpInstance.slider.sync(swInstance.slider);
            mpInstance.mount();
            swInstance.mount();

            var clampThumbList = function (index) {
              var slider = swInstance.slider;
              if (!slider || !slider.track || !slider.list) return;
              var trackWidth = slider.track.clientWidth || 0;
              var listWidth = slider.list.scrollWidth || 0;

              if (listWidth <= trackWidth) {
                slider.list.style.transform = 'none';
                return;
              }

              var maxOffset = Math.min(0, trackWidth - listWidth);

              var targetIdx = typeof index === 'number' ? index : slider.index;
              var slide = slider.Components && slider.Components.Slides ? slider.Components.Slides.getAt(targetIdx) : null;
              var targetX = slide && slide.slide ? -slide.slide.offsetLeft : 0;

              var clampedX = Math.max(targetX, maxOffset);
              clampedX = Math.min(0, clampedX);

              if (slider.Components && slider.Components.Move) {
                slider.Components.Move.translate(clampedX);
              }
            };

            swInstance.slider.on('mounted updated resize', function () {
              clampThumbList();
            });

            swInstance.slider.on('move', function (index) {
              clampThumbList(index);
            });

            swInstance.slider.on('moved', function () {
              clampThumbList();
            });
            if (swOpts.isNavigation) {
              swInstance.slider.realSlides.forEach(function (el, i) {
                el.style.cursor = 'pointer';
                el.addEventListener('click', function () {
                  mpInstance.slider.go(i, { userInitiated: true });
                });
              });
            }
            this.slider = mpInstance.slider;
            this._syncSlider = swInstance.slider;
            break;
          }
          case 'centered':
          case 'default': {
            var elem = this.querySelector('.splide');
            if (!elem) break;
            var options = JSON.parse(elem.getAttribute('data-splide'));
            var isCentered = this.dataset.sliderType === 'centered';
            var instance = initializeSlider(elem, options, !isCentered);
            var slider = instance.slider;
            if (isCentered) {
              slider.on('mounted updated resize', function () {
                var isOverflow = slider.isOverflow();
                var list = slider.list;
                if (!isOverflow) {
                  var isMobile = window.innerWidth < 768;
                  var desktopAlign = 'var(--justify-value-xl, ' + (self.dataset.alignmentDesktop || 'center') + ')';
                  var mobileAlign = 'var(--justify-value-xs, ' + (self.dataset.alignmentMobile || 'center') + ')';
                  list.style.justifyContent = isMobile ? mobileAlign : desktopAlign;
                } else {
                  list.style.justifyContent = '';
                }
              });
              instance.mount();
            }
            if (this.dataset.goTo === 'true') {
              document.addEventListener('shopify:block:select', function (event) {
                if (!event.target.classList.contains('splide__slide')) return;
                var found = slider.slides.find(function (s) { return s.el.id === event.target.id; });
                if (!found) return;
                slider.go(found.index);
              });
            }
            this.slider = slider;
            break;
          }
          default:
            break;
        }

        var setupHover = function () {
          var deferredMediaElements = self.querySelectorAll('deferred-media');
          if (deferredMediaElements.length === 0) return;
          var slides = self.querySelectorAll('.splide__slide');
          slides.forEach(function (slide) {
            var deferredMedia = slide.querySelector('deferred-media');
            if (!deferredMedia || deferredMedia.dataset.onHover !== 'true') return;
            var playMedia = function () {
              var video = deferredMedia.querySelector('video');
              if (video) {
                video.muted = true;
                video.play().catch(function () {});
              }
              var iframe = deferredMedia.querySelector('iframe');
              if (iframe) {
                if (iframe.classList.contains('js-youtube')) {
                  iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
                } else if (iframe.classList.contains('js-vimeo')) {
                  iframe.contentWindow.postMessage('{"method":"play"}', '*');
                }
              }
            };
            slide.addEventListener('mouseenter', function () {
              if (deferredMedia.getAttribute('loaded') !== 'true' && deferredMedia.loadMediaHandler) {
                deferredMedia.loadMediaHandler();
              }
              playMedia();
            });
            slide.addEventListener('mouseleave', function () {
              CarouselSliderEl.inactiveEvent({ slide: slide });
            });
          });
        };

        var setupVideoAutoplay = function () {
          if (self.dataset.videoAutoplay !== 'true' || !self.slider) return;
          var slider = self.slider;
          var playCurrentSlideVideo = function () {
            var currentSlide = slider.slides[slider.index];
            if (!currentSlide) return;
            var deferredMedia = currentSlide.el.querySelector('deferred-media');
            if (!deferredMedia) return;
            if (deferredMedia.getAttribute('loaded') !== 'true' && deferredMedia.loadMediaHandler) {
              deferredMedia.loadMediaHandler();
            }
            var video = currentSlide.el.querySelector('video');
            if (video) {
              video.muted = true;
              video.play().catch(function () {});
              video.onended = function () { slider.go('>'); };
            } else {
              var iframe = currentSlide.el.querySelector('iframe');
              if (iframe) {
                if (iframe.classList.contains('js-youtube')) {
                  iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
                } else if (iframe.classList.contains('js-vimeo')) {
                  iframe.contentWindow.postMessage('{"method":"play"}', '*');
                }
              }
            }
          };
          slider.on('moved mounted', playCurrentSlideVideo);
          if (slider.state.isIdle) playCurrentSlideVideo();
        };

        var initializedInteractions = false;
        var handleIntersection = function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              if (!initializedInteractions) {
                setupHover();
                setupVideoAutoplay();
                initializedInteractions = true;
              } else if (self.slider && self.dataset.videoAutoplay === 'true') {
                var currentSlide = self.slider.slides[self.slider.index];
                if (currentSlide) {
                  var deferredMedia = currentSlide.el.querySelector('deferred-media');
                  if (deferredMedia && deferredMedia.getAttribute('loaded') === 'true') {
                    var video = currentSlide.el.querySelector('video');
                    if (video) video.play().catch(function () {});
                  }
                }
              }
            } else {
              if (initializedInteractions && self.slider) {
                var currentSlide2 = self.slider.slides[self.slider.index];
                if (currentSlide2) CarouselSliderEl.inactiveEvent(currentSlide2);
              }
            }
          });
        };
        this._observer = new IntersectionObserver(handleIntersection, { threshold: 0.1 });
        this._observer.observe(this);
      }
    }
    CarouselSliderEl.inactiveEvent = function (currentSlide) {
      window.pauseElementBasedMedia(currentSlide.slide || currentSlide.el);
    };
    customElements.define('carousel-slider', CarouselSliderEl);
  }

  whenGsapReady(defineCarouselSlider);
})();
